import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import AppShellLayout from '../components/AppShellLayout';
import { IconLink, IconCheck } from '../components/Icons';
import { capture } from '../analytics';

type Status = 'idle' | 'connecting' | 'exchanging' | 'ingesting' | 'success' | 'error';

export default function ConnectPage() {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const [gmailStatus, setGmailStatus]         = useState<Status>('idle');
  const [microsoftStatus, setMicrosoftStatus] = useState<Status>('idle');
  const [emailCount, setEmailCount]           = useState<number | null>(null);
  const [errorMessage, setErrorMessage]       = useState<string>('');

  // Exchange OAuth code after redirect back
  useEffect(() => {
    const code     = searchParams.get('code');
    const state    = searchParams.get('state') ?? '';
    const provider = state.startsWith('ms-') ? 'microsoft' : 'gmail';
    if (!code) return;

    window.history.replaceState({}, '', '/app/connect');

    if (provider === 'microsoft') {
      setMicrosoftStatus('exchanging');
      fetch('/api/auth/microsoft/callback', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ code, state }),
        credentials: 'include',
      })
        .then(async res => {
          if (!res.ok) throw new Error(await res.text());
          return res.json();
        })
        .then(data => {
          capture('microsoft_connect_completed');
          if (data.isIngesting) setMicrosoftStatus('ingesting');
          else { setEmailCount(data.emailCount ?? null); setMicrosoftStatus('success'); }
        })
        .catch(err => {
          console.error(err);
          setErrorMessage(t('connect.microsoftError'));
          setMicrosoftStatus('error');
        });
    } else {
      setGmailStatus('exchanging');
      fetch('/api/auth/gmail/callback', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ code, state }),
        credentials: 'include',
      })
        .then(async res => {
          if (!res.ok) throw new Error(await res.text());
          return res.json();
        })
        .then(data => {
          capture('gmail_connect_completed');
          if (data.isIngesting) setGmailStatus('ingesting');
          else { setEmailCount(data.emailCount ?? null); setGmailStatus('success'); }
        })
        .catch(err => {
          console.error(err);
          setErrorMessage(t('connect.gmailError'));
          setGmailStatus('error');
        });
    }
  }, [searchParams]); // eslint-disable-line react-hooks/exhaustive-deps

  // Poll for ingestion completion
  useEffect(() => {
    if (gmailStatus !== 'ingesting') return;
    const interval = setInterval(async () => {
      try {
        const res = await fetch('/api/auth/gmail/status', { credentials: 'include' });
        if (!res.ok) return;
        const data = await res.json();
        if (!data.isIngesting) { setEmailCount(data.emailCount ?? null); setGmailStatus('success'); }
      } catch { /* keep polling */ }
    }, 30000);
    return () => clearInterval(interval);
  }, [gmailStatus]);

  useEffect(() => {
    if (microsoftStatus !== 'ingesting') return;
    const interval = setInterval(async () => {
      try {
        const res = await fetch('/api/auth/microsoft/status', { credentials: 'include' });
        if (!res.ok) return;
        const data = await res.json();
        if (!data.isIngesting) { setEmailCount(data.emailCount ?? null); setMicrosoftStatus('success'); }
      } catch { /* keep polling */ }
    }, 30000);
    return () => clearInterval(interval);
  }, [microsoftStatus]);

  const handleConnectGmail = async () => {
    setGmailStatus('connecting');
    capture('gmail_connect_started');
    try {
      const res = await fetch('/api/auth/gmail/start', { credentials: 'include' });
      if (!res.ok) throw new Error();
      const { authUrl } = await res.json();
      window.location.href = authUrl;
    } catch {
      setErrorMessage(t('connect.connectGmailError'));
      setGmailStatus('error');
    }
  };

  const handleConnectMicrosoft = async () => {
    setMicrosoftStatus('connecting');
    capture('microsoft_connect_started');
    try {
      const res = await fetch('/api/auth/microsoft/start', { credentials: 'include' });
      if (!res.ok) throw new Error();
      const { authUrl } = await res.json();
      window.location.href = authUrl;
    } catch {
      setErrorMessage(t('connect.connectMicrosoftError'));
      setMicrosoftStatus('error');
    }
  };

  const renderProviderCard = (
    provider: 'gmail' | 'microsoft',
    status: Status,
    onConnect: () => void,
    label: string,
  ) => (
    <div className="connect-card">
      {status === 'idle' && (
        <>
          <div className="connect-card-icon"><IconLink size={22} /></div>
          <h2>{t('connect.cardTitle', { label })}</h2>
          <p>{t('connect.cardBody', { label })}</p>
          <button className="btn-primary" onClick={onConnect}>
            {t('connect.connectButton', { label })}
          </button>
        </>
      )}

      {status === 'connecting' && (
        <>
          <div className="connect-card-icon"><IconLink size={22} /></div>
          <h2>{provider === 'gmail' ? t('connect.redirectingGoogle') : t('connect.redirectingMicrosoft')}</h2>
          <p>{t('connect.authorising')}</p>
        </>
      )}

      {status === 'exchanging' && (
        <>
          <div className="connect-card-icon"><IconLink size={22} /></div>
          <h2>{t('connect.connecting')}</h2>
          <p>{t('connect.verifying')}</p>
        </>
      )}

      {status === 'ingesting' && (
        <>
          <div className="connect-card-icon"><IconLink size={22} /></div>
          <h2>{t('connect.analysing')}</h2>
          <p>{t('connect.analysingBody')}</p>
          <div className="spinner" aria-label="Loading" style={{ marginTop: '16px' }} />
        </>
      )}

      {status === 'success' && (
        <div className="connect-success">
          <div className="connect-success-icon"><IconCheck size={22} /></div>
          <h2>{t('connect.successTitle', { label })}</h2>
          <p>
            {emailCount !== null
              ? t('connect.successBodyCount', { count: emailCount })
              : t('connect.successBodyNoCount')}
          </p>
          <button
            className="btn-primary"
            style={{ marginTop: '8px' }}
            onClick={() => navigate('/app/dashboard')}
          >
            {t('connect.goToDashboard')}
          </button>
        </div>
      )}

      {status === 'error' && (
        <>
          <div className="connect-card-icon" style={{ background: 'var(--danger-bg)', color: 'var(--danger)' }}>
            <IconLink size={22} />
          </div>
          <h2>{t('connect.failedTitle')}</h2>
          <p style={{ color: 'var(--danger)' }}>{errorMessage}</p>
          <button className="btn-primary" onClick={() => { setGmailStatus('idle'); setMicrosoftStatus('idle'); }}>
            {t('connect.tryAgain')}
          </button>
        </>
      )}
    </div>
  );

  return (
    <AppShellLayout>
      <div className="page-header">
        <h1>{t('connect.title')}</h1>
        <p>{t('connect.subtitle')}</p>
      </div>

      {renderProviderCard('gmail', gmailStatus, handleConnectGmail, 'Gmail')}
      {renderProviderCard('microsoft', microsoftStatus, handleConnectMicrosoft, 'Microsoft')}
    </AppShellLayout>
  );
}
